import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { UserService } from "@/services/users/UserService";
import { Roles } from '@/types/auth/auth';
import { useAccountBranding } from "@/hooks/branding/useAccountBranding";

const TeamBrandingSummary = () => {
  const { branding, hasAccountBranding, isLoading } = useAccountBranding();

  if (isLoading) return null;

  if (!hasAccountBranding) {
    return (
      <div>
        <label className="block mb-1.5 font-medium text-sm">Team Branding</label>
        <div className="flex items-center justify-between p-2.5 rounded-md border border-gray-300 bg-gray-50 text-sm text-[#7C8B9C]">
          <span>No team branding configured</span>
          <Link to="/genie-hub/account-branding" className="text-genie-primary font-medium">
            Configure branding
          </Link>
        </div>
      </div>
    );
  }

  const colors = [branding?.primaryColor, branding?.secondaryColor, branding?.backgroundColor, branding?.textColor].filter(
    (color): color is string => !!color
  );

  return (
    <div>
      <label className="block mb-1.5 font-medium text-sm">Team Branding</label>
      <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-gray-300 bg-gray-50">
        <div className="flex items-center gap-3">
          {branding?.logoUrl && (
            <img src={branding.logoUrl} alt="Team logo" className="h-8 w-8 rounded object-contain bg-white border border-gray-200" />
          )}
          {colors.length > 0 && (
            <div className="flex gap-1">
              {colors.map((color, index) => (
                <div
                  key={index}
                  className="h-6 w-6 rounded-full border border-gray-300"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}
        </div>
        <Link to="/genie-hub/account-branding" className="text-genie-primary font-medium text-sm">
          Edit branding
        </Link>
      </div>
    </div>
  );
};

interface TeamManagementSectionProps {
  onTeamUpdate?: () => void;
}

export const TeamManagementSection = ({ onTeamUpdate }: TeamManagementSectionProps) => {
  const { user, userProfile, team, refreshTeam } = useAuth();
  const [teamName, setTeamName] = useState("");
  const [teamBrand, setTeamBrand] = useState(""); // preserved for updateTeam; brand field is hidden from UI
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (team) {
      setTeamName(team.name || "");
      setTeamBrand(team.brand || "");
    }
  }, [team]);

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!teamName.trim()) {
      toast.error("Team name is required.");
      setLoading(false);
      return;
    }

    if (!user?.id) {
      toast.error("User not found.");
      setLoading(false);
      return;
    }

    // Show loading toast
    const loadingToast = toast.loading("Creating your team...");

    try {
      await UserService.createTeam(teamName, userProfile?.full_name || teamName, user.id);

      setIsCreatingTeam(false);
      await refreshTeam();
      onTeamUpdate?.();

      toast.success("Team created successfully! You're now the team owner.", { id: loadingToast });
    } catch (err: any) {
      toast.error(`Failed to create team: ${err.message}`, { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!team || !user) return;

    setLoading(true);
    const loadingToast = toast.loading("Updating team details...");

    try {
      await UserService.updateTeam(team.id, teamName, teamBrand);

      setIsEditingTeam(false);
      await refreshTeam();

      toast.success("Team updated successfully!", { id: loadingToast });
    } catch (err: any) {
      toast.error(`Failed to update team: ${err.message}`, { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex justify-between items-center mb-[18px]">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <Users className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold m-0">{team?.owner_id === user?.id ? "Team Management" : "My Team"}</h2>
        </div>
        {team && !isEditingTeam && team.owner_id === user?.id && (
          <button
            onClick={() => setIsEditingTeam(true)}
            className="bg-transparent border border-[#4E9CFF] px-2 py-1 rounded text-xs cursor-pointer text-genie-primary"
          >
            Edit
          </button>
        )}
      </div>

      {!team ? (
        <>
          {!isCreatingTeam ? (
            <div className="text-center text-[#7C8B9C]">
              <p className="m-0 mb-[15px]">You haven't created a team yet.</p>
              {userProfile?.role === Roles.Admin || userProfile?.role === Roles.Business ? (
                <button
                  onClick={() => setIsCreatingTeam(true)}
                  className="text-white border-none rounded-md px-4 py-2 font-semibold cursor-pointer text-sm bg-genie-primary"
                >
                  Create Team
                </button>
              ) : (
                <div className="p-2.5 bg-[#FEF3C7] rounded-md border border-[#FDE047] text-sm text-[#92400E]">
                  Team creation is only available for business users.
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleCreateTeam} className="flex flex-col gap-[14px]">
              <div>
                <label className="block mb-1.5 font-medium text-sm">Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full p-2.5 rounded-md border border-gray-300 text-sm"
                  required
                />
              </div>
              <div className="flex gap-2.5">
                <button
                  type="submit"
                  disabled={loading}
                  className="text-white border-none rounded-md px-4 py-2 font-semibold cursor-pointer text-sm bg-genie-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Creating..." : "Create Team"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreatingTeam(false)}
                  disabled={loading}
                  className="bg-transparent border border-gray-300 text-gray-600 px-4 py-2 rounded-md cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      ) : (
        <>
          {!isEditingTeam ? (
            <div className="flex flex-col gap-3">
              <div>
                <label className="block mb-1.5 font-medium text-sm">Team Name</label>
                <input
                  type="text"
                  value={team?.name || ""}
                  disabled
                  className="w-full p-[9px] rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed text-sm"
                />
              </div>
              <TeamBrandingSummary />
            </div>
          ) : team?.owner_id === user?.id ? (
            <form onSubmit={handleUpdateTeam} className="flex flex-col gap-[14px]">
              <div>
                <label className="block mb-1.5 font-medium text-sm">Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full p-2.5 rounded-md border border-gray-300 text-sm"
                  required
                />
              </div>
              <div className="flex gap-2.5">
                <button
                  type="submit"
                  disabled={loading}
                  className="text-white border-none rounded-md px-4 py-2 font-semibold cursor-pointer text-sm bg-genie-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingTeam(false);
                    setTeamName(team?.name || "");
                  }}
                  disabled={loading}
                  className="bg-transparent border border-gray-300 text-gray-600 px-4 py-2 rounded-md cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              <div>
                <label className="block mb-1.5 font-medium text-sm">Team Name</label>
                <input
                  type="text"
                  value={team?.name || ""}
                  disabled
                  className="w-full p-[9px] rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed text-sm"
                />
              </div>
              <TeamBrandingSummary />
              <div className="p-2.5 bg-[#FFF9C4] rounded-md border border-[#FDE047] text-sm text-[#854D0E]">
                You are a member of this team. Only the team owner can edit team settings.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};